/**
 * Partner prices — Admin-only list of clinics with links to edit prices per clinic.
 * Partners manage their prices from the front; this page gives admins quick access to all clinic prices.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
  PaginationComponent,
  RSelect,
} from "../../../components/Component";
import { useHistory, Link } from "react-router-dom";
import { FormGroup, Input, Label } from "reactstrap";
import http from "../../../utils/http-common";
import { getPublicUrl } from "../../../utils/Utils";

const ITEM_PER_PAGE = 10;

const PartnerPricesList = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clinics, setClinics] = useState([]);
  const [partners, setPartners] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [filterPartner, setFilterPartner] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const fetchClinics = useCallback(() => {
    setLoading(true);
    setError("");
    http
      .get("/api/v1/partners/medical/clinics")
      .then((res) => {
        const list = res.data?.data || res.data?.clinics || res.data || [];
        setClinics(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        setError(e.response?.data?.message || "Failed to load clinics");
        setClinics([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchClinics();
  }, [fetchClinics]);

  useEffect(() => {
    http
      .get("/api/v1/medical-partners", { params: { limit: 500 } })
      .then((res) => {
        const raw = res.data?.data ?? res.data ?? [];
        setPartners(Array.isArray(raw) ? raw : []);
      })
      .catch(() => setPartners([]));
  }, []);

  const countries = useMemo(() => {
    if (!Array.isArray(clinics)) return [];
    const list = [...new Set(clinics.map((c) => c?.address?.country).filter(Boolean))].sort();
    return list.map((c) => ({ value: c, label: c }));
  }, [clinics]);

  const partnerOptions = useMemo(() => {
    return partners.map((p) => ({
      value: p._id,
      label: p.organization_name || p.contact_person_email || p._id,
    }));
  }, [partners]);

  const getPartnerName = (clinic) => {
    const p = clinic?.partnerId || clinic?.partner;
    if (!p) return "—";
    return p.organization_name || p.contact_person_email || "—";
  };

  const filteredClinics = useMemo(() => {
    if (!Array.isArray(clinics)) return [];
    let data = [...clinics];
    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();
      data = data.filter((c) => {
        const name = (c?.name || "").toLowerCase();
        const partnerName = getPartnerName(c).toLowerCase();
        const country = (c?.address?.country || "").toLowerCase();
        return name.includes(q) || partnerName.includes(q) || country.includes(q);
      });
    }
    if (filterCountry) {
      data = data.filter((c) => c?.address?.country === filterCountry);
    }
    if (filterPartner) {
      data = data.filter((c) => {
        const id = (c?.partnerId?._id || c?.partnerId || c?.partner?._id || c?.partner)?.toString();
        return id === filterPartner;
      });
    }
    return data;
  }, [clinics, searchText, filterCountry, filterPartner]);

  const indexOfLastItem = currentPage * ITEM_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEM_PER_PAGE;
  const currentItems = useMemo(
    () => filteredClinics.slice(indexOfFirstItem, indexOfLastItem),
    [filteredClinics, indexOfFirstItem, indexOfLastItem]
  );
  const totalPages = Math.ceil(filteredClinics.length / ITEM_PER_PAGE) || 1;

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handleEditPrices = (clinicId) => {
    history.push(getPublicUrl(`clinics/${clinicId}/prices`));
  };

  const handleResetFilters = () => {
    setSearchText("");
    setFilterCountry("");
    setFilterPartner("");
    setCurrentPage(1);
  };

  const hasActiveFilters = searchText.trim() || filterCountry || filterPartner;

  return (
    <>
      <Head title="Partner prices" />
      <Content>
        <Block size="lg">
          <BlockBetween className="flex-wrap g-2">
            <BlockHead>
              <BlockHeadContent>
                <BlockTitle tag="h4">Partner prices</BlockTitle>
                <BlockDes>
                  <p>Manage prices per clinic. Use filters to find a clinic, then click &quot;Edit prices&quot; to set prices by specialty.</p>
                </BlockDes>
              </BlockHeadContent>
            </BlockHead>
            <ul className="nk-block-tools g-3">
              <li>
                <Button color="light" outline onClick={fetchClinics} disabled={loading} title="Refresh list">
                  <Icon name="refresh" />
                  <span>Refresh</span>
                </Button>
              </li>
              <li>
                <Button tag={Link} color="primary" to={getPublicUrl("clinics")}>
                  <Icon name="home" />
                  <span>View all clinics</span>
                </Button>
              </li>
            </ul>
          </BlockBetween>

          {/* Filters */}
          {!loading && clinics.length > 0 && (
            <div className="card card-bordered card-preview mb-3">
              <div className="card-inner">
                <div className="row g-3">
                  <div className="col-md-4">
                    <FormGroup>
                      <Label className="form-label">Search</Label>
                      <Input
                        type="text"
                        placeholder="Clinic, partner, or country..."
                        value={searchText}
                        onChange={(e) => {
                          setSearchText(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="form-control"
                        aria-label="Search clinics by name, partner or country"
                      />
                    </FormGroup>
                  </div>
                  <div className="col-md-3">
                    <FormGroup>
                      <Label className="form-label">Country</Label>
                      <RSelect
                        options={[{ value: "", label: "All countries" }, ...countries]}
                        value={filterCountry ? { value: filterCountry, label: filterCountry } : { value: "", label: "All countries" }}
                        onChange={(opt) => {
                          setFilterCountry(opt?.value ?? "");
                          setCurrentPage(1);
                        }}
                        aria-label="Filter by country"
                      />
                    </FormGroup>
                  </div>
                  <div className="col-md-3">
                    <FormGroup>
                      <Label className="form-label">Partner</Label>
                      <RSelect
                        options={[{ value: "", label: "All partners" }, ...partnerOptions]}
                        value={
                          filterPartner
                            ? partnerOptions.find((o) => o.value === filterPartner) || { value: filterPartner, label: "Partner" }
                            : { value: "", label: "All partners" }
                        }
                        onChange={(opt) => {
                          setFilterPartner(opt?.value ?? "");
                          setCurrentPage(1);
                        }}
                        aria-label="Filter by partner"
                      />
                    </FormGroup>
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    {hasActiveFilters && (
                      <Button color="light" size="md" onClick={handleResetFilters}>
                        Reset filters
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {loading && (
            <div className="text-center py-5" role="status" aria-live="polite">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading clinics...</span>
              </div>
              <p className="mt-2 text-soft">Loading clinics...</p>
            </div>
          )}

          {error && (
            <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
              <span>{error}</span>
              <Button color="danger" outline size="sm" onClick={fetchClinics}>
                Retry
              </Button>
            </div>
          )}

          {!loading && !error && (
            <>
              <DataTable className="card-stretch">
                <DataTableHead>
                  <DataTableRow>
                    <DataTableItem as="th" scope="col">Clinic</DataTableItem>
                    <DataTableItem as="th" scope="col">Partner</DataTableItem>
                    <DataTableItem as="th" scope="col">Country</DataTableItem>
                    <DataTableItem as="th" scope="col" className="text-end">Action</DataTableItem>
                  </DataTableRow>
                </DataTableHead>
                <DataTableBody>
                  {currentItems.length === 0 ? (
                    <DataTableRow>
                      <DataTableItem colSpan={4} className="text-center py-5">
                        {filteredClinics.length === 0 && clinics.length > 0 ? (
                          <>
                            <p className="text-soft mb-2">No clinics match the current filters.</p>
                            <Button color="light" size="sm" onClick={handleResetFilters}>
                              Reset filters
                            </Button>
                          </>
                        ) : (
                          <>
                            <p className="text-soft mb-2">No clinics found.</p>
                            <Button tag={Link} color="primary" outline size="sm" to={getPublicUrl("clinics")}>
                              Go to Clinics
                            </Button>
                          </>
                        )}
                      </DataTableItem>
                    </DataTableRow>
                  ) : (
                    currentItems.map((clinic) => (
                      <DataTableRow key={clinic._id}>
                        <DataTableItem>
                          <span className="fw-medium">{clinic.name || "—"}</span>
                        </DataTableItem>
                        <DataTableItem>{getPartnerName(clinic)}</DataTableItem>
                        <DataTableItem>{clinic.address?.country || "—"}</DataTableItem>
                        <DataTableItem className="text-end">
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleEditPrices(clinic._id)}
                            className="btn-dim"
                            aria-label={`Edit prices for ${clinic.name || "clinic"}`}
                          >
                            <Icon name="edit" />
                            <span>Edit prices</span>
                          </Button>
                        </DataTableItem>
                      </DataTableRow>
                    ))
                  )}
                </DataTableBody>
              </DataTable>

              {currentItems.length > 0 && totalPages > 1 && (
                <div className="card-inner">
                  <PaginationComponent
                    itemPerPage={ITEM_PER_PAGE}
                    totalItems={filteredClinics.length}
                    paginate={setCurrentPage}
                    currentPage={currentPage}
                  />
                </div>
              )}
            </>
          )}
        </Block>
      </Content>
    </>
  );
};

export default PartnerPricesList;
